import { useState } from 'react';
import { Building, Map, Clock, User, Calendar } from 'lucide-react';

// Mock data for Buldhana infrastructure projects (only 6 important projects)
const buldhanaProjects = [
  {
    id: 1,
    title: "Buldhana Water Supply Project",
    description: "Comprehensive water supply system upgrade to provide clean drinking water to all residential areas in Buldhana.",
    location: "Buldhana City",
    status: "In Progress",
    completion: "65%",
    startDate: "Mar 2024",
    endDate: "Sep 2025",
    manager: "Rajesh Patil",
    image: "/home image/Buldhana Water Supply Project.avif"
  },
  {
    id: 2,
    title: "Buldhana Smart Road Network",
    description: "Development of smart road network with LED lighting, CCTV monitoring, and improved traffic management systems.",
    location: "Buldhana District",
    status: "In Progress",
    completion: "42%",
    startDate: "Jan 2024",
    endDate: "Dec 2025",
    manager: "Priya Deshmukh",
    image: "/home image/Buldhana Smart Road Network.jpg"
  },
  {
    id: 3,
    title: "Buldhana Renewable Energy Park",
    description: "Establishing solar and wind energy park to provide sustainable power solutions for Buldhana region.",
    location: "Eastern Buldhana",
    status: "In Progress",
    completion: "30%",
    startDate: "Nov 2023",
    endDate: "Apr 2026",
    manager: "Amit Sharma",
    image: "/home image/Buldhana Renewable Energy Park.avif"
  },
  {
    id: 4,
    title: "Buldhana Medical Complex",
    description: "Construction of a modern medical complex with 200-bed hospital, emergency services, and specialized care units.",
    location: "Central Buldhana",
    status: "In Progress",
    completion: "55%",
    startDate: "Feb 2024",
    endDate: "Jan 2026",
    manager: "Sunita Jadhav",
    image: "/home image/medical complex.png"
  },
  {
    id: 5,
    title: "Buldhana Digital Infrastructure",
    description: "Enhancing digital connectivity across Buldhana with fiber optic networks and public Wi-Fi zones.",
    location: "Buldhana District",
    status: "In Progress",
    completion: "40%",
    startDate: "Feb 2024",
    endDate: "Jan 2026",
    manager: "Prakash Mehta",
    image: "/home image/Digital Infrastructure.jpg"
  },
  {
    id: 6,
    title: "Buldhana Agricultural Market Complex",
    description: "Modern agricultural market with storage facilities, direct farmer-to-consumer platforms, and export linkages.",
    location: "Western Buldhana",
    status: "In Progress",
    completion: "35%",
    startDate: "Mar 2024",
    endDate: "Jul 2026",
    manager: "Anita Wagh",
    image: "/home image/Buldhana Agricultural.avif"
  }
];

// Component for the project detail card
const ProjectCard = ({ project }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col h-full">
      <div className="relative h-48">
        <img 
          src={project.image} 
          alt={project.title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
          <div className="p-4 text-white">
            <h3 className="text-xl font-bold">{project.title}</h3>
            <div className="flex items-center space-x-2 text-sm">
              <Map size={14} />
              <span>{project.location}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 flex-grow">
        <p className="text-gray-600 mb-4">{project.description}</p>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Completion</span>
            <span className="text-sm font-semibold">{project.completion}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-yellow-400 h-2 rounded-full" 
              style={{ width: project.completion }}
            ></div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="flex items-center space-x-1 text-sm text-gray-600">
              <Calendar size={14} />
              <span>{project.startDate} - {project.endDate}</span>
            </div>
            <div className="flex items-center space-x-1 text-sm text-gray-600">
              <User size={14} />
              <span>{project.manager}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <button className="w-full py-2 bg-yellow-400 text-black font-medium rounded-lg hover:bg-yellow-500 transition">
          View Details
        </button>
      </div>
    </div>
  );
};

// Main Buldhana Infrastructure Projects component
export default function BuldhanaInfrastructureProjects() {
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-center space-x-3 mb-4">
            <Building className="text-yellow-400" size={32} />
            <h1 className="text-4xl font-bold">Buldhana Infrastructure Projects</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl">
            Explore key development initiatives transforming Buldhana. These major projects 
            represent our commitment to improving infrastructure across the region.
          </p>
          <div className="mt-8 flex items-center space-x-2">
            <div className="px-4 py-2 bg-yellow-400 text-black font-medium rounded-lg">
              6 Major Projects
            </div>
            <div className="px-4 py-2 bg-gray-700 rounded-lg text-gray-300">
              Essential Infrastructure Development
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Project Highlights */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Featured Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {buldhanaProjects.slice(0, 3).map(project => (
              <div key={project.id} className="relative rounded-lg overflow-hidden h-64 group">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-bold">{project.title}</h3>
                  <p className="text-gray-200 mt-2">{project.description.substring(0, 100)}...</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* All Buldhana Projects */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">All Key Projects</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {buldhanaProjects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </div>
      
      {/* Contact Section */}
      <div className="bg-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Have Questions About Our Projects?</h2>
            <p className="text-gray-600 mb-6">
              Contact the Buldhana Infrastructure Development Office for more information about any of these projects.
            </p>
            <button className="py-2 px-6 bg-yellow-400 text-black font-medium rounded-lg hover:bg-yellow-500 transition">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}